import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import type { CampaignDraftData } from "../page";

interface Props {
  onBack: () => void;
  draft: CampaignDraftData;
}

export default function StepReview({ onBack, draft }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [consentChecked, setConsentChecked] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchedId, setLaunchedId] = useState<string | null>(null);

  const handleLaunch = async () => {
    if (!consentChecked) return;
    setIsLaunching(true);

    try {
      const res = await fetchApi<any>("/campaigns", {
        method: "POST",
        body: JSON.stringify({
          name: draft.name || "New Lead Campaign",
          type: draft.type || "Promotional",
          channel: draft.channel || "WhatsApp",
          status: draft.sendOption === "now" ? "Active" : "Scheduled",
          target_audience: draft.targetAudience || "All Leads",
          leads_count: draft.leadCount || 247,
          scheduled_at: draft.sendOption === "later" && draft.scheduleDate ? `${draft.scheduleDate}T${draft.scheduleTime || "10:00"}:00Z` : null,
        }),
      });

      const newId = res?.data?.id || res?.id;
      showToast("Campaign created & launched successfully!", "success");

      setLaunchedId(String(newId || "done"));
      setTimeout(() => {
        navigate("/campaigns");
      }, 1200);
    } catch (err: any) {
      showToast(err.message || "Failed to launch campaign", "error");
      setIsLaunching(false);
    }
  };

  if (launchedId) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-check-line text-3xl text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Campaign Launched!</h2>
        <p className="text-sm text-gray-500 mb-4">Redirecting to campaigns list...</p>
        <div className="w-8 h-8 mx-auto border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Review before sending</h2>

        {/* Summary rows */}
        <div className="divide-y divide-gray-100">
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Campaign Name</p>
              <p className="text-xs text-indigo-600 font-medium">{draft.name || "Untitled Campaign"} · {draft.type || "Promotional"}</p>
            </div>
            <button onClick={onBack} className="text-xs text-indigo-600 hover:underline font-semibold cursor-pointer">Edit</button>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Audience</p>
              <p className="text-xs text-gray-500">{draft.leadCount} leads · {draft.targetAudience}</p>
            </div>
            <button onClick={onBack} className="text-xs text-indigo-600 hover:underline font-semibold cursor-pointer">Edit</button>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Channel & Template</p>
              <p className="text-xs text-gray-500">{draft.channel} · {draft.templateName || "Standard Broadcast"}</p>
            </div>
            <button onClick={onBack} className="text-xs text-indigo-600 hover:underline font-semibold cursor-pointer">Edit</button>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Schedule</p>
              <p className="text-xs text-gray-500">{draft.sendOption === "now" ? "Send immediately" : `Scheduled for ${draft.scheduleDate || 'later'}`}</p>
            </div>
            <button onClick={onBack} className="text-xs text-indigo-600 hover:underline font-semibold cursor-pointer">Edit</button>
          </div>
        </div>

        {/* Estimated stats */}
        <div className="mt-5 bg-indigo-50 border border-indigo-200 rounded-2xl p-4">
          <p className="text-xs font-bold text-indigo-800 uppercase tracking-wider mb-2">Estimated Performance</p>
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Estimated reach</span>
              <span className="font-semibold text-gray-900">{draft.leadCount} leads</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Est. delivery rate</span>
              <span className="font-semibold text-green-700">~95% ({Math.floor(draft.leadCount * 0.95)} leads)</span>
            </div>
          </div>
        </div>

        {/* Consent notice */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-start gap-2.5">
            <i className="ri-error-warning-line text-amber-600 text-sm mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-800">Compliance Confirmation</p>
              <p className="text-xs text-amber-700 mt-0.5">Ensure selected leads have consented to receiving messages via {draft.channel}.</p>
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            />
            <span className="text-xs font-semibold text-gray-800">I confirm leads have consented</span>
          </label>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          ← Back
        </button>
        <button
          onClick={handleLaunch}
          disabled={!consentChecked || isLaunching}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold transition-all whitespace-nowrap cursor-pointer shadow-sm"
        >
          {isLaunching ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-rocket-line" />}
          <span>{isLaunching ? "Launching..." : "Launch Campaign"}</span>
        </button>
      </div>
    </div>
  );
}